/*
 * AG-UI Compatible Agent Implementation
 * Emits AG-UI protocol events as an Observable for the UI to consume
 * No UI state, no logging, no duplicate state management
 */

import { v4 as uuidv4 } from 'uuid';
import { Observable } from 'rxjs';
import { EventType } from '@ag-ui/client';

const API_HOST = process.env.REACT_APP_LANGCHAIN_API_HOST || 'localhost';
const API_PORT = process.env.REACT_APP_LANGCHAIN_API_PORT || '8000';
const API_PATH = process.env.REACT_APP_LANGCHAIN_API_PATH || '/api/langchain';
const API_BASE_URL = `http://${API_HOST}:${API_PORT}${API_PATH}`;

export default class AGUIAgent {
  constructor(config = {}) {
    this.agentId = config.agentId || uuidv4();
    this.threadId = config.threadId || uuidv4();
    this.baseUrl = config.baseUrl || API_BASE_URL;
  }

  runAgent(input = {}) {
    const runId = input.runId || uuidv4();
    return new Observable(observer => {
      this.processQuery(input, runId, observer)
        .catch(error => {
          observer.next({
            type: EventType.RUN_ERROR,
            message: error.message,
            code: error.code || 'UNKNOWN_ERROR',
            timestamp: Date.now()
          });
          observer.error(error);
        });
    });
  }

  async processQuery(input, runId, observer) {
    try {
      const messages = input.messages || [];
      const userMessage = messages.filter(m => m.role === 'user').pop();
      if (!userMessage) throw new Error('No user message found');
      const queryMode = input.context?.queryMode || 'mcp';
      const endpoint = queryMode === 'llm' ? '/llm-chat' : '/query';
      const requestBody = {
        threadId: this.threadId,
        runId: runId,
        messages,
        tools: queryMode === 'mcp' ? (input.tools || []) : [],
        context: input.context || []
      };
      // Use Fetch API with POST and manual SSE parsing for both LLM and MCP modes
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      if (!response.body) throw new Error('No response body for SSE');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let events = buffer.split('\n\n');
          buffer = events.pop();
          for (const eventData of events) {
            if (!eventData.trim()) continue;
            const dataMatch = eventData.match(/^data: (.+)$/m);
            if (!dataMatch) continue;
            try {
              const event = JSON.parse(dataMatch[1]);
              observer.next(event);
              if (event.type === EventType.RUN_FINISHED) {
                observer.complete();
                return;
              }
              if (event.type === EventType.RUN_ERROR) {
                observer.error(new Error(event.message));
                return;
              }
            } catch (err) {
              observer.next({
                type: EventType.RUN_ERROR,
                message: 'Event parse error',
                code: 'PARSE_ERROR',
                timestamp: Date.now()
              });
              observer.error(err);
              return;
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      return;
    } catch (error) {
      observer.next({
        type: EventType.RUN_ERROR,
        message: error.message,
        code: error.code || 'PROCESS_QUERY_ERROR',
        timestamp: Date.now()
      });
      observer.error(error);
    }
  }
}
